import { AppsRoutes } from '../getRoute'
import { PropsWithChildren, MouseEvent } from 'react'
import { RouteProps } from '../types'
import { Params } from '../getParams'
import { getFullRoute, useAppsNavigate } from '../useAppsNavigate'
import { staticState } from '@wellesley/host-remote/common/staticState'
import { normalizeAtPathForBrowser } from '../pathNormalizer'

type TargetBlank = '_blank'
type ValidatedTo<T extends TargetBlank | undefined> = T extends undefined
  ? AppsRoutes | ''
  : string

export type AppsLinkProps<
  T = RouteProps,
  P extends Params = Params,
  Target extends TargetBlank | undefined = undefined,
> = {
  target?: Target
  to: ValidatedTo<Target>
  rel?: string
  props?: T
  queryParams?: P
  className?: string
  disabled?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void | boolean
  draggable?: boolean
  'data-testid'?: string
}

export const getFullHref = (...args: Parameters<typeof getFullRoute>): string =>
  normalizeAtPathForBrowser(getFullRoute(...args))

export const AppsLink = <
  T extends RouteProps = RouteProps,
  P extends Params = Params,
  Target extends TargetBlank | undefined = undefined,
>({
  to,
  props,
  children,
  target,
  queryParams,
  className,
  disabled,
  onClick,
  ...linkProps
}: PropsWithChildren<AppsLinkProps<T, P, Target>>) => {
  const navigate = useAppsNavigate()
  const fullHref = getFullHref(to as AppsRoutes | '', {
    queryParams,
    props,
  })

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    if (disabled) return

    const skipNavigation = onClick?.(e) ?? false

    if (skipNavigation) return

    navigate(to as AppsRoutes | '', {
      queryParams,
      props,
      target: e.ctrlKey || e.metaKey ? '_blank' : target,
    })
  }

  const { baseUrl } = staticState.get()
  const href = target ? fullHref : `${baseUrl}${fullHref}`

  return (
    <a
      href={href}
      target={target}
      onClick={handleClick}
      className={className}
      {...linkProps}
    >
      {children}
    </a>
  )
}
